import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { wordsApi, type WordWithTags } from '@/api/words'
import { useEnumsStore } from '@/stores/enumsStore'
import { useAuthStore } from '@/stores/authStore'
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Link as MuiLink,
  Grid,
  Breadcrumbs,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon, Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import '@/styles/dashboard.css'

const FIELDS: Array<{ key: keyof WordWithTags; labelKey: string }> = [
  { key: 'transcription', labelKey: 'words.transcription' },
  { key: 'translation', labelKey: 'words.translation' },
  { key: 'part_of_speech', labelKey: 'cards.filter.part_of_speech' },
  { key: 'type', labelKey: 'cards.filter.type' },
  { key: 'example_sentence', labelKey: 'cards.word_attrs.example_sentence' },
  { key: 'synonym', labelKey: 'cards.word_attrs.synonym' },
  { key: 'antonym', labelKey: 'cards.word_attrs.antonym' },
  { key: 'note', labelKey: 'cards.word_attrs.note' },
]

function typeLabel(value: string | undefined, t: (k: string) => string): string {
  if (!value) return '—'
  if (value === 'word') return t('cards.filter_type_word')
  if (value === 'phrase') return t('cards.filter_type_phrase')
  return value
}

function partOfSpeechLabel(value: string | undefined, partOfSpeechOptions: Array<{ value: string; label: string }>): string {
  if (!value) return '—'
  const opt = partOfSpeechOptions.find((o) => o.value === value)
  return opt?.label ?? value
}

export function WordViewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [word, setWord] = useState<WordWithTags | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const enums = useEnumsStore((s) => s.enums)
  const loadEnums = useEnumsStore((s) => s.loadEnums)
  const currentUser = useAuthStore((s) => s.user)
  const partOfSpeechOptions = enums?.PartOfSpeech ?? []

  useEffect(() => {
    loadEnums().catch(() => {})
  }, [loadEnums])

  useEffect(() => {
    if (id) {
      const numId = parseInt(id, 10)
      if (!Number.isNaN(numId)) {
        wordsApi
          .getWord(numId)
          .then(setWord)
          .catch((err) => setError(err?.message || t('words.loadError')))
          .finally(() => setLoading(false))
      } else {
        setLoading(false)
      }
    }
  }, [id, t])

  if (!id) {
    return null
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <MuiLink component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <MuiLink component={RouterLink} to={ROUTES.WORDS} underline="hover">
            {t('words.title')}
          </MuiLink>
          <Typography color="text.primary">{word?.word ?? '—'}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton
            size="small"
            onClick={() => navigate(ROUTES.WORDS)}
            aria-label={t('common.back')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('words.word')}
          </Typography>
          <PageHelpPopover pageKey="words" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Card>
            <CardContent>
              <Skeleton variant="text" width="40%" height={32} />
              <Skeleton variant="text" width="60%" sx={{ mt: 2 }} />
              <Skeleton variant="text" width="80%" />
            </CardContent>
          </Card>
        ) : word ? (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                {word.word}
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {FIELDS.slice(0, 4).map(({ key, labelKey }) => {
                      const value = word[key]
                      if (key === 'part_of_speech') {
                        return (
                          <Box key={key}>
                            <Typography variant="body2" color="text.secondary">
                              {t(labelKey)}
                            </Typography>
                            <Typography variant="body1">
                              {partOfSpeechLabel(value as string, partOfSpeechOptions)}
                            </Typography>
                          </Box>
                        )
                      }
                      if (key === 'type') {
                        return (
                          <Box key={key}>
                            <Typography variant="body2" color="text.secondary">
                              {t(labelKey)}
                            </Typography>
                            <Typography variant="body1">
                              {typeLabel(value as string, t)}
                            </Typography>
                          </Box>
                        )
                      }
                      return (
                        <Box key={key}>
                          <Typography variant="body2" color="text.secondary">
                            {t(labelKey)}
                          </Typography>
                          <Typography variant="body1">
                            {(value as string) || '—'}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {FIELDS.slice(4, 8).map(({ key, labelKey }) => {
                      const value = word[key]
                      return (
                        <Box key={key}>
                          <Typography variant="body2" color="text.secondary">
                            {t(labelKey)}
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {(value as string) || '—'}
                          </Typography>
                        </Box>
                      )
                    })}

                    {word.tags && word.tags.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('nav.tags')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {word.tags.map((tag) => (
                            <Typography
                              key={tag.id}
                              variant="body2"
                              component="span"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                              }}
                            >
                              {tag.name}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('words.createdBy')}
                      </Typography>
                      {word.creator ? (
                        currentUser && word.creator.id === currentUser.id ? (
                          <MuiLink
                            component={RouterLink}
                            to="/profile"
                            underline="hover"
                            sx={{ fontWeight: 500 }}
                          >
                            {t('common.you')}
                          </MuiLink>
                        ) : (
                          <MuiLink
                            component={RouterLink}
                            to={`/users/${word.creator.id}`}
                            underline="hover"
                            sx={{ fontWeight: 500 }}
                          >
                            {word.creator.surname && word.creator.first_name
                              ? `${word.creator.surname} ${word.creator.first_name}`
                              : word.creator.email ?? String(word.creator.id)}
                          </MuiLink>
                        )
                      ) : (
                        <Typography variant="body1">—</Typography>
                      )}
                    </Box>

                    {typeof word.users_count === 'number' && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('words.usageCount')}
                        </Typography>
                        <Typography variant="body1">
                          {word.users_count}
                        </Typography>
                      </Box>
                    )}

                    {(word.dictionaries?.length ?? word.dictionaries_count) ? (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('words.inDictionaries')}
                        </Typography>
                        {word.dictionaries && word.dictionaries.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {word.dictionaries.map((d) => (
                              <MuiLink
                                key={d.id}
                                component={RouterLink}
                                to={ROUTES.DICTIONARY_VIEW(d.id)}
                                underline="hover"
                                sx={{ fontWeight: 500, mr: 1 }}
                              >
                                {d.name}
                              </MuiLink>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1">
                            {word.dictionaries_count}
                          </Typography>
                        )}
                      </Box>
                    ) : null}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : (
          <Typography color="text.secondary">
            {t('words.noWords')}
          </Typography>
        )}
      </Box>
    </DashboardLayout>
  )
}
